import React, { useEffect, useState } from "react";
import {
  Form,
  Col,
  Row,
  message,
  Select,
  Upload,
  Button,
  Card,
  Input,
  InputNumber,
} from "antd";
import { API_METHODS, BLANK_VALUE } from "../../constants";
import useApi from "../../hooks/useApi";
import { InboxOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

function UploadNotes() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [subscription_types, setSubscriptionTypes] = useState([]);
  const [selected_subscription_type, setSelectedSubscriptionType] =
    useState(null);
  const [isFreeNotes, setIsFreeNotes] = useState(0);
  const [messageApi, contextHolder] = message.useMessage();
  const [disableForm, setDisableForm] = useState(false);

  const [response, request] = useApi(
    "/admin/notes/upload",
    null,
    API_METHODS.POST,
    true // for multipart data
  );

  const [subjectResponse, getSubjectRequest] = useApi(
    `/admin/subject/dropdown`,
    null,
    API_METHODS.GET
  );

  const [subscriptionTypesResponse, getSubscriptionTypes] = useApi(
    `/admin/video/subscription-type-dropdown`,
    null,
    API_METHODS.GET
  );

  useEffect(() => {
    getSubscriptionTypes();
  }, []);

  useEffect(() => {
    const data =
      subscriptionTypesResponse.data !== BLANK_VALUE
        ? subscriptionTypesResponse.data
        : [];
    setSubscriptionTypes(data);
  }, [subscriptionTypesResponse]);

  useEffect(() => {
    const data =
      subjectResponse.data !== BLANK_VALUE ? subjectResponse.data : [];
    setSubjects(data);
  }, [subjectResponse]);

  useEffect(() => {
    if (response.data) {
      setDisableForm(false);
      messageApi.open({
        type: "success",
        content: "Notes added successfully",
      });
      navigate("/portal/notes");
    } else if (response.error) {
      setDisableForm(false);
      messageApi.open({
        type: "error",
        content: response.error,
      });
      navigate("/portal/notes");
    }
  }, [response]);

  const onFinish = (values) => {
    setDisableForm(true);
    const formData = new FormData();
    for (const name in values) {
      if (name === "file") {
        if (values["file"] !== undefined) {
          formData.append(
            "file",
            values["file"].file.originFileObj,
            values["file"].file.name
          );
        }
      } else {
        formData.append(name, values[name]);
      }
    }
    request(formData);
  };

  const uploadProps = {
    beforeUpload: (file) => {
      const isPdf = file.type === "application/pdf";
      if (!isPdf) {
        messageApi.open({
          type: "error",
          content: "Attach PDF only",
        });
        return Upload.LIST_IGNORE;
      }
      return true;
    },
  };

  const dummyRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  const subscriptionSelected = (subscription_type_id) => {
    setSelectedSubscriptionType(subscription_type_id);
    if (subscription_type_id == 3) {
      getSubjectRequest();
    }
  };

  const isFreeSelected = (value) => {
    setIsFreeNotes(value);
    if (value == 1) {
      setSelectedSubscriptionType(null);
      form.setFieldValue("subscription_type_id", null);
    }
  };

  return (
    <Card title="Upload Notes">
      {contextHolder}
      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        initialValues={{ is_free: 0 }}
        disabled={disableForm}
      >
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="is_free"
              label="Is Free Notes"
              rules={[
                {
                  required: true,
                  message: "Is Free Notes is required.",
                },
              ]}
            >
              <Select
                options={[
                  {
                    value: 1,
                    label: "Yes",
                  },
                  {
                    value: 0,
                    label: "No",
                  },
                ]}
                onSelect={isFreeSelected}
              />
            </Form.Item>
          </Col>
          {isFreeNotes != 1 && (
            <Col span={8}>
              <Form.Item
                name="subscription_type_id"
                label="Subscription Type"
                rules={[
                  {
                    required: true,
                    message: "Subscription Type is required.",
                  },
                ]}
              >
                <Select
                  placeholder="Select Subscription Type"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  onSelect={subscriptionSelected}
                >
                  {subscription_types?.map((option) => (
                    <Select.Option key={option.id} value={option.id}>
                      {option.subscription_name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}
          {selected_subscription_type === 3 && (
            <Col span={8}>
              <Form.Item
                name="subject_id"
                label="Subject Name"
                rules={[
                  { required: true, message: "Subject Name is required." },
                ]}
              >
                <Select
                  placeholder="Select Subject"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {subjects?.map((option) => (
                    <Select.Option key={option.id} value={option.id}>
                      {option.subject_name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}
          <Col span={8}>
            <Form.Item
              name="tittle"
              label="Tittle"
              rules={[{ required: true, message: "Tittle is required." }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="file"
              label="Upload File"
              rules={[{ required: true, message: "File is required." }]}
            >
              <Upload.Dragger
                customRequest={dummyRequest}
                {...uploadProps}
                maxCount={1}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Click or drag file to this area to upload
                </p>
              </Upload.Dragger>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="page_number"
              label="Page Number For Thumbnail"
              rules={[
                {
                  required: true,
                  message: "Page Number For Thumbnail is required.",
                },
              ]}
            >
              <InputNumber />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item style={{ float: "right" }}>
          <Button type="primary" htmlType="submit">
            Submit
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
}

export default UploadNotes;
